import React, { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Eye, RefreshCw, Bot, Mail, User, Building, Calendar, Tag, MessageSquare, Clock, CheckCircle, XCircle, AlertCircle, Send, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { format } from 'date-fns'
import type { AutoReply } from '@/hooks/useAutoReplies'
import { DataTable, type DataTableColumn, type DataTableView } from '@/components/shared/DataTable'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { List, X } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

interface RepliesTableProps {
  replies: AutoReply[]
  filteredReplies: AutoReply[]
  totalReplies: number
  onReplyClick: (reply: AutoReply) => void
  onCategorize?: (replyId: string) => void
  categorizingIds?: Set<string>
  isLoading?: boolean
  isFetching?: boolean
  currentPage?: number
  totalPages?: number
  itemsPerPage?: number
  onPageChange?: (page: number) => void
  onItemsPerPageChange?: (itemsPerPage: number) => void
  searchTerm?: string
  onSearchChange?: (term: string) => void
  clientFilter?: string
  onClientFilterChange?: (filter: string) => void
  categorizationFilter?: string
  onCategorizationFilterChange?: (filter: string) => void
  sdrStatusFilter?: string
  onSdrStatusFilterChange?: (filter: string) => void
  dateRange?: { from: Date | undefined; to: Date | undefined }
  onDateRangeChange?: (range: { from: Date | undefined; to: Date | undefined }) => void
  uniqueClients?: Array<{ id: string; name: string }>
  onSendTestNotification?: (replyId: string, notificationType: 'positive' | 'negative') => Promise<void>
  sendingTestNotificationId?: string | null
}

// Column widths are in percentages when enableTextWrapping=true
// Total visible columns should be 100% (no Actions column)
const DEFAULT_COLUMNS: DataTableColumn<AutoReply>[] = [
  { id: 'lead', label: 'Lead', visible: true, sortable: true, width: 13, order: 0 },
  { id: 'from_email', label: 'Sender', visible: true, sortable: true, width: 12, order: 1 },
  { id: 'campaign_details', label: 'Campaign Details', visible: true, sortable: true, width: 12, order: 2, icon: <Building className="h-4 w-4 text-muted-foreground" /> },
  { id: 'reply_count', label: 'Reply Count', visible: true, sortable: true, width: 7, order: 3, icon: <MessageSquare className="h-4 w-4 text-muted-foreground" /> },
  { id: 'category_and_type', label: 'Category / Type', visible: true, sortable: true, width: 10, order: 4, icon: <Tag className="h-4 w-4 text-muted-foreground" /> },
  { id: 'ai_model_and_time', label: 'AI Model / Time', visible: true, sortable: true, width: 9, order: 5, icon: <Bot className="h-4 w-4 text-muted-foreground" /> },
  { id: 'reply_preview', label: 'Reply', visible: true, sortable: false, width: 20, order: 6, icon: <MessageSquare className="h-4 w-4 text-muted-foreground" /> },
  { id: 'time_replied', label: 'Time Replied', visible: true, sortable: true, width: 9, order: 7, icon: <Calendar className="h-4 w-4 text-muted-foreground" /> },
  { id: 'sdr_status', label: 'SDR Status', visible: true, sortable: true, width: 8, order: 8, icon: <Clock className="h-4 w-4 text-muted-foreground" /> },
  { id: 'subject', label: 'Subject', visible: false, sortable: true, width: 12, order: 9 },
  { id: 'reply_body', label: 'Message', visible: false, sortable: false, width: 18, order: 10, icon: <MessageSquare className="h-4 w-4 text-muted-foreground" /> },
  { id: 'sentiment', label: 'Sentiment', visible: false, sortable: true, width: 8, order: 11, icon: <Tag className="h-4 w-4 text-muted-foreground" /> },
]

export function RepliesTable({
  replies,
  filteredReplies,
  totalReplies,
  onReplyClick,
  onCategorize,
  categorizingIds = new Set(),
  isLoading = false,
  isFetching = false,
  currentPage = 1,
  totalPages = 1,
  itemsPerPage = 15,
  onPageChange,
  onItemsPerPageChange,
  searchTerm = '',
  onSearchChange,
  clientFilter = 'all',
  onClientFilterChange,
  categorizationFilter = 'all',
  onCategorizationFilterChange,
  sdrStatusFilter = 'all',
  onSdrStatusFilterChange,
  dateRange,
  onDateRangeChange,
  uniqueClients = [],
  onSendTestNotification,
  sendingTestNotificationId
}: RepliesTableProps) {
  const [columns, setColumns] = useState<DataTableColumn<AutoReply>[]>(DEFAULT_COLUMNS)
  const [sortField, setSortField] = useState<string>('time_replied')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [currentView, setCurrentView] = useState<string>('all-replies')
  const [showFilterDialog, setShowFilterDialog] = useState(false)
  const [showViewSidebar, setShowViewSidebar] = useState(false)

  const savedViews: DataTableView[] = [
    { id: 'all-replies', name: 'All Replies', type: 'table' },
    { id: 'categorized', name: 'Categorized', type: 'table' },
    { id: 'uncategorized', name: 'Uncategorized', type: 'table' },
  ]

  // Format date helper
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-'
    try {
      return format(new Date(dateString), 'MMM d, yyyy HH:mm')
    } catch {
      return '-'
    }
  }

  // Get category badge variant (no longer using colors for categories)
  const getCategoryBadgeVariant = (category: string | undefined) => {
    return 'outline'
  }

  // Clean preview text - remove HTML tags, unnecessary whitespace, and common email artifacts
  const cleanPreviewText = (text: string | null | undefined): string => {
    if (!text) return '-'
    
    // Remove HTML tags (including self-closing tags)
    let cleaned = text.replace(/<[^>]+>/g, '')
    
    // Decode common HTML entities
    const entityMap: Record<string, string> = {
      '&nbsp;': ' ',
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&apos;': "'",
      '&ndash;': '–',
      '&mdash;': '—',
      '&hellip;': '...',
    }
    
    Object.entries(entityMap).forEach(([entity, replacement]) => {
      cleaned = cleaned.replace(new RegExp(entity, 'gi'), replacement)
    })
    
    // Decode numeric HTML entities (&#123; format)
    cleaned = cleaned.replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
    
    // Remove common email artifacts and signatures
    cleaned = cleaned
      .replace(/On\s+\w+,\s+\w+\s+\d+,\s+\d+\s+at\s+\d+:\d+.*?wrote:.*/gis, '') // "On Mon, Jan 1, 2024 at 10:00 AM wrote:"
      .replace(/From:.*?To:.*?Subject:.*/gis, '') // Email headers
      .replace(/Sent from.*/gi, '') // "Sent from iPhone" etc.
      .replace(/Get Outlook for.*/gi, '') // Outlook signatures
      .replace(/This email was sent to.*/gi, '') // Email footers
      .replace(/Best regards,.*/gis, '') // Common signatures
      .replace(/Regards,.*/gis, '') // Common signatures
      .replace(/Thanks,.*/gis, '') // Common signatures
      .replace(/--\s*\n.*/gs, '') // Email signatures after --
      .replace(/________________________________.*/gis, '') // Separator lines
    
    // Clean up whitespace
    cleaned = cleaned
      .replace(/\s+/g, ' ') // Multiple spaces to single space
      .replace(/\n\s*\n\s*\n/g, '\n\n') // Multiple newlines to double newline max
      .replace(/^\s+|\s+$/gm, '') // Trim each line
      .trim()
    
    // Remove very short or meaningless content
    if (cleaned.length < 3) {
      return '-'
    }
    
    // Limit length for display (but allow wrapping)
    if (cleaned.length > 500) {
      cleaned = cleaned.substring(0, 500).trim() + '...'
    }
    
    return cleaned || '-'
  }

  // Render cell content
  const renderCell = (reply: AutoReply, columnId: string) => {
    switch (columnId) {
      case 'lead':
        // Lead column: Show both to_name and to_email
        // Check if this is a LinkedIn reply (has LinkedIn-specific fields)
        const isLinkedInReply = !!(reply as any).lead_full_name || !!(reply as any).lead_profile_url || !!(reply as any).recent_messages || !!(reply as any).sender_profile_url
        
        if (isLinkedInReply) {
          // LinkedIn reply: use lead_full_name and lead_profile_url
          const leadName = (reply as any).lead_full_name
          const leadProfileUrl = (reply as any).lead_profile_url
          
          return (
            <div className="flex flex-col gap-1">
              {leadName && (
                <div className="font-medium text-sm break-words whitespace-normal">
                  {leadProfileUrl ? (
                    <a 
                      href={leadProfileUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {leadName}
                    </a>
                  ) : (
                    leadName
                  )}
                </div>
              )}
              {leadProfileUrl && (
                <div className="text-xs text-muted-foreground break-words whitespace-normal">
                  <a 
                    href={leadProfileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                  >
                    {leadProfileUrl}
                  </a>
                </div>
              )}
              {!leadProfileUrl && (
                <div className="text-xs text-muted-foreground break-words whitespace-normal">
                  <span className="break-all">-</span>
                </div>
              )}
            </div>
          )
        } else {
          // Email reply: use to_name and to_email (original logic - DO NOT CHANGE)
        return (
          <div className="flex flex-col gap-1">
            {reply.to_name && (
              <div className="font-medium text-sm break-words whitespace-normal">{reply.to_name}</div>
            )}
              <div className="text-xs text-muted-foreground break-words whitespace-normal">
              <span className="break-all">{reply.to_email || '-'}</span>
            </div>
          </div>
        )
        }
      case 'from_email':
        // Sender column: Show both from_name and from_email (like Lead column)
        // Check if this is a LinkedIn reply (has LinkedIn-specific fields)
        const isLinkedInSender = !!(reply as any).sender_full_name || !!(reply as any).sender_profile_url || !!(reply as any).recent_messages || !!(reply as any).lead_profile_url
        
        if (isLinkedInSender) {
          // LinkedIn reply: use sender_full_name and sender_profile_url (NO EMAIL)
          const senderName = (reply as any).sender_full_name
          const senderProfileUrl = (reply as any).sender_profile_url
          
          return (
            <div className="flex flex-col gap-1">
              {senderName && (
                <div className="font-medium text-sm break-words whitespace-normal">
                  {senderProfileUrl ? (
                    <a 
                      href={senderProfileUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {senderName}
                    </a>
                  ) : (
                    senderName
                  )}
                </div>
              )}
              {senderProfileUrl && (
                <div className="text-xs text-muted-foreground break-words whitespace-normal">
                  <a 
                    href={senderProfileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                  >
                    {senderProfileUrl}
                  </a>
                </div>
              )}
              {!senderProfileUrl && (
                <div className="text-xs text-muted-foreground break-words whitespace-normal">
                  <span className="break-all">-</span>
                </div>
              )}
            </div>
          )
        } else {
          // Email reply: use from_name and from_email (original logic - DO NOT CHANGE)
        return (
          <div className="flex flex-col gap-1">
            {reply.from_name && (
              <div className="font-medium text-sm break-words whitespace-normal">{reply.from_name}</div>
            )}
              <div className="text-xs text-muted-foreground break-words whitespace-normal">
              <span className="break-all">{reply.from_email || '-'}</span>
            </div>
          </div>
        )
        }
      case 'campaign_details':
        // Campaign Details: Show both campaign name and ID
        return (
          <div className="flex flex-col gap-1">
            {reply.campaign_name && (
              <div className="font-medium text-sm break-words whitespace-normal">
                {reply.campaign_name}
              </div>
            )}
            {reply.campaign_id && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Building className="h-3 w-3" />
                ID: {reply.campaign_id}
              </div>
            )}
            {!reply.campaign_name && !reply.campaign_id && '-'}
          </div>
        )
      case 'reply_count':
        return reply.reply_count !== null && reply.reply_count !== undefined ? (
          <Badge variant="outline" className="font-medium">{reply.reply_count}</Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      case 'category_and_type':
        // Combined Category and Reply Type column
        // Show empty if not categorized instead of "Not categorized"
        if (!reply.response_category && !reply.reply_category_type) {
          return <span className="text-muted-foreground">-</span>
        }
        
        // Get color variant for reply category type based on chart colors
        // We use 'outline' as base variant and override with custom colors for all types
        const getReplyTypeVariant = (type: string | null | undefined) => {
          // Use 'outline' as base, then override with custom classes
          return 'outline'
        }
        
        // Get custom color classes for all reply types based on chart colors
        const getReplyTypeColorClass = (type: string | null | undefined) => {
          if (!type) return ''
          const normalizedType = type.toLowerCase()
          
          // Color mapping based on chart:
          // Positive: Green
          // Negative: Red/Pink
          // Neutral: Orange/Yellow
          // Auto-Reply: Grey
          // OOO: Purple
          
          if (normalizedType === 'positive') {
            return 'bg-green-100 text-green-800 border-green-300'
          }
          if (normalizedType === 'negative') {
            return 'bg-red-100 text-red-800 border-red-300'
          }
          if (normalizedType === 'neutral') {
            return 'bg-orange-100 text-orange-800 border-orange-300'
          }
          if (normalizedType === 'auto-reply' || normalizedType === 'autoreply') {
            return 'bg-gray-100 text-gray-800 border-gray-300'
          }
          if (normalizedType === 'ooo' || normalizedType === 'out of office') {
            return 'bg-purple-100 text-purple-800 border-purple-300'
          }
          return ''
        }
        
        return (
          <div className="flex flex-col gap-3">
            {reply.response_category && (
              <Badge 
                variant="outline"
                className="w-fit"
              >
                {reply.response_category}
              </Badge>
            )}
            {reply.reply_category_type && (
              <Badge 
                variant={getReplyTypeVariant(reply.reply_category_type)}
                className={`w-fit ${getReplyTypeColorClass(reply.reply_category_type)}`}
              >
                {reply.reply_category_type}
              </Badge>
            )}
          </div>
        )
      case 'ai_model_and_time':
        // Combined AI Model and Categorization Time
        if (!reply.ai_model_used && !reply.categorized_at) {
          return <span className="text-muted-foreground">-</span>
        }
        
        return (
          <div className="flex flex-col gap-1">
            {reply.ai_model_used && (
              <div className="flex items-center gap-1">
                <Bot className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-medium">{reply.ai_model_used}</span>
              </div>
            )}
            {reply.categorized_at && (
              <div className="text-xs text-muted-foreground">
                {format(new Date(reply.categorized_at), 'MMM d, HH:mm')}
              </div>
            )}
          </div>
        )
      case 'reply_preview':
        // Reply column using preview_text (cleaned) - fixed height with overflow
        // Check if this is a LinkedIn reply with recent_messages
        const isLinkedInReplyForPreview = !!(reply as any).recent_messages && Array.isArray((reply as any).recent_messages)
        
        let replyText: string
        if (isLinkedInReplyForPreview) {
          // LinkedIn reply: use recent_messages where is_reply === true, get the latest one
          const replyMessages = (reply as any).recent_messages.filter((msg: any) => msg.is_reply === true)
          if (replyMessages.length > 0) {
            // Get the most recent reply (last in array)
            const latestReply = replyMessages[replyMessages.length - 1]
            replyText = latestReply.message || latestReply.text || latestReply.content || ''
          } else {
            replyText = ''
          }
        } else {
          // Email reply: use preview_text or reply_body (original logic - DO NOT CHANGE)
          replyText = reply.preview_text || reply.reply_body || ''
        }
        
        const cleanedPreview = cleanPreviewText(replyText)
        return (
          <div className="text-sm break-words whitespace-normal text-muted-foreground h-20 overflow-y-auto">
            {cleanedPreview}
          </div>
        )
      case 'reply_category_type':
        // Keep for backward compatibility (hidden column)
        return reply.reply_category_type ? (
          <Badge 
            variant={
              reply.reply_category_type === 'positive' ? 'default' :
              reply.reply_category_type === 'negative' ? 'destructive' :
              reply.reply_category_type === 'OOO' ? 'secondary' :
              'outline'
            }
          >
            {reply.reply_category_type}
          </Badge>
        ) : (
          <Badge variant="outline">-</Badge>
        )
      case 'response_category':
        // Keep for backward compatibility (hidden column)
        return reply.response_category ? (
          <Badge variant={getCategoryBadgeVariant(reply.response_category)}>
            {reply.response_category}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      case 'ai_model_used':
        // Keep for backward compatibility (hidden column)
        return reply.ai_model_used ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <Bot className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-medium">{reply.ai_model_used}</span>
            </div>
            {reply.categorized_at && (
              <div className="text-xs text-muted-foreground">
                {format(new Date(reply.categorized_at), 'MMM d, HH:mm')}
              </div>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      case 'subject':
        return (
          <div className="font-medium truncate text-sm" title={reply.subject}>
            {reply.subject || '-'}
          </div>
        )
      case 'reply_body':
        const preview = reply.reply_body || reply.preview_text || '-'
        return (
          <div className="truncate text-sm text-muted-foreground" title={preview}>
            {preview}
          </div>
        )
      case 'time_replied':
        // Time Replied column - use time_replied field, fallback to event_timestamp or created_at
        const timeReplied = reply.time_replied || reply.event_timestamp || reply.created_at
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            {formatDate(timeReplied)}
          </div>
        )
      case 'sdr_status':
        // SDR Status column - only show for positive replies
        if (reply.reply_category_type !== 'positive') {
          return <span className="text-muted-foreground text-sm">-</span>
        }
        
        const hasResponded = reply.average_time_to_respond !== null && reply.average_time_to_respond !== undefined
        const waitingForReply = reply.lead_without_a_reply === true
        const status = reply.sdr_response_tracking_status || 'not_tracked'
        
        if (hasResponded) {
          // SDR has responded
          // average_time_to_respond is stored in SECONDS, not minutes
          // FIX: Validate numeric value to prevent NaN display and handle negative values
          const responseTimeSeconds = typeof reply.average_time_to_respond === 'number' && !isNaN(reply.average_time_to_respond) && reply.average_time_to_respond >= 0
            ? reply.average_time_to_respond
            : 0
          
          // If we have a negative value, it means the matching logic found an SDR message before the reply
          // This shouldn't happen, but we'll display it as "Unknown" or use absolute value
          const days = Math.floor(Math.abs(responseTimeSeconds) / 86400)
          const hours = Math.floor((Math.abs(responseTimeSeconds) % 86400) / 3600)
          const minutes = Math.floor((Math.abs(responseTimeSeconds) % 3600) / 60)
          
          // Format compactly for table display
          let timeDisplay = ''
          if (responseTimeSeconds === 0 && reply.average_time_to_respond !== null && reply.average_time_to_respond < 0) {
            timeDisplay = 'Invalid'
          } else if (days > 0) {
            timeDisplay = `${days}d ${hours}h`
          } else if (hours > 0) {
            timeDisplay = `${hours}h ${minutes}m`
          } else {
            timeDisplay = `${minutes}m`
          }
          
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">Responded</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {timeDisplay}
              </div>
              {reply.first_sdr_response_timestamp && (
                <div className="text-xs text-muted-foreground">
                  {format(new Date(reply.first_sdr_response_timestamp), 'MMM d, HH:mm')}
                </div>
              )}
            </div>
          )
        } else if (waitingForReply || status === 'tracking') {
          // Waiting for SDR response
          const positiveReplyTime = reply.first_positive_reply_timestamp || reply.categorized_at || reply.created_at
          const timeSinceReply = positiveReplyTime 
            ? Math.round((new Date().getTime() - new Date(positiveReplyTime).getTime()) / (1000 * 60))
            : null
          
          const hours = timeSinceReply ? Math.floor(timeSinceReply / 60) : 0
          const minutes = timeSinceReply ? timeSinceReply % 60 : 0
          const timeDisplay = hours > 0 ? `${hours}h ${minutes}m` : minutes > 0 ? `${minutes}m` : 'Just now'
          
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-700">Waiting</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {timeDisplay} ago
              </div>
              {reply.last_sdr_poll_at && (
                <div className="text-xs text-muted-foreground">
                  Last checked: {format(new Date(reply.last_sdr_poll_at), 'MMM d, HH:mm')}
                </div>
              )}
            </div>
          )
        } else {
          // Not tracked yet
          // Check if this is a LinkedIn reply (has lead_profile_url) or Email reply (has sl_email_lead_id)
          const isLinkedInReply = !!(reply as any).lead_profile_url || !!(reply as any).event_type?.includes('linkedin')
          const hasRequiredFields = isLinkedInReply
            ? reply.campaign_id && (reply as any).lead_profile_url && (reply as any).sender_profile_url
            : reply.campaign_id && (reply as any).sl_email_lead_id && (reply as any).secret_key
          
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                {hasRequiredFields ? (
                  <>
                    <AlertCircle className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-muted-foreground">Not tracked</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-red-400" />
                    <span className="text-sm text-red-600">Missing fields</span>
                  </>
                )}
              </div>
            </div>
          )
        }
      case 'created_at':
        // Keep for backward compatibility (hidden column)
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            {formatDate(reply.event_timestamp || reply.created_at)}
          </div>
        )
      case 'sentiment':
        return reply.sentiment ? (
          <Badge variant={reply.sentiment === 'positive' ? 'default' : 'secondary'}>
            {reply.sentiment}
          </Badge>
        ) : (
          '-'
        )
      default:
        return '-'
    }
  }

  // Render filters
  const renderFilters = () => {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select value={categorizationFilter} onValueChange={onCategorizationFilterChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="categorized">Categorized</SelectItem>
              <SelectItem value="not_categorized">Not Categorized</SelectItem>
              <SelectItem value="positive">Positive Replies</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Client</Label>
          <Select value={clientFilter} onValueChange={onClientFilterChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {uniqueClients.map(client => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>SDR Status (Positive Replies Only)</Label>
          <Select value={sdrStatusFilter} onValueChange={onSdrStatusFilterChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All SDR Status</SelectItem>
              <SelectItem value="responded">Responded</SelectItem>
              <SelectItem value="waiting">Waiting</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    )
  }

  return (
    <DataTable<AutoReply>
      data={replies}
      filteredData={filteredReplies}
      totalItems={totalReplies}
      columns={columns}
      defaultColumns={DEFAULT_COLUMNS}
      onColumnsChange={setColumns}
      renderCell={renderCell}
      onRowClick={onReplyClick}
      getItemId={(reply) => reply.id}
      sortField={sortField}
      sortDirection={sortDirection}
      onSortChange={(field, direction) => {
        setSortField(field)
        setSortDirection(direction)
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      itemsPerPage={itemsPerPage}
      onPageChange={onPageChange}
      onItemsPerPageChange={onItemsPerPageChange}
      isLoading={isLoading}
      isFetching={isFetching}
      views={savedViews}
      currentView={currentView}
      onViewChange={setCurrentView}
      onCreateView={() => setShowViewSidebar(true)}
      renderFilters={renderFilters}
      showFilterDialog={showFilterDialog}
      onFilterDialogChange={setShowFilterDialog}
      emptyStateTitle="No replies found"
      emptyStateDescription="Try adjusting your filters"
      emptyStateIcon={<Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />}
      enableTextWrapping={true}
    />
  )
}
